import type { ApiResponse, ApiError } from '@socialsoup/shared';
import { useAuthStore } from '@/stores/authStore';
import { refreshAccessToken } from '@/lib/auth';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

/** Error code for a non-2xx response whose body is not the API error envelope. */
export const UNEXPECTED_RESPONSE = 'UNEXPECTED_RESPONSE';

export class ApiRequestError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly details: unknown;

  constructor(error: ApiError['error'], status: number) {
    super(error.message);
    this.name = 'ApiRequestError';
    this.code = error.code;
    this.status = status;
    this.details = error.details;
  }
}

/**
 * The in-flight refresh, SCOPED TO THE SESSION GENERATION that started it
 * (theme-customization A3).
 *
 * It used to be one process-wide `isRefreshing` flag, which is a correctness bug once accounts
 * can change inside a tab: a 401 handler belonging to the previous account would join — or
 * start — a refresh and then commit its result over the current session. Keying the promise by
 * generation means concurrent 401s in the SAME session still share one round-trip (the reason
 * the flag existed), while a 401 from an older session shares nothing and refreshes nothing.
 */
let refreshInFlight: { generation: number; promise: Promise<string | null> } | null = null;

function refreshForGeneration(
  generation: number,
  ownership: { owns: () => boolean; userId: string | null },
): Promise<string | null> {
  if (refreshInFlight && refreshInFlight.generation === generation) return refreshInFlight.promise;
  const promise = refreshAccessToken(ownership).finally(() => {
    if (refreshInFlight?.promise === promise) refreshInFlight = null;
  });
  refreshInFlight = { generation, promise };
  return promise;
}

/**
 * Per-call transport options.
 *
 * `revoke` is ONE option, not two, and that is the point (A3 security gate). It started as a
 * separate `authToken` plus `disableAuthRefresh`, which permitted a combination no caller wants:
 * a foreign bearer WITH automatic refresh, where a 401 on the foreign token would spend the live
 * session's rotation and write the result into it. Nothing reachable from user input could
 * request that — logout is the only caller and it always passed both — but the shape allowed it,
 * so the shape is gone: asking to send captured credentials IS asking not to refresh.
 *
 * `public` follows the same rule (sso-login Phase 4). It is the one option for a request that
 * belongs to NO session — the SSO provider list and the SSO handoff exchange: send no bearer at
 * all, and never refresh, replay or `clearAuth` on a 401. `revoke` cannot serve there because it
 * still sends a bearer, and a stale bearer on a sign-in request would let a 401 spend the old
 * session's rotation. The two are mutually exclusive in the type, so no caller can ask for
 * "no credentials" and "these credentials" at once.
 */
type CredentialMode =
  | {
      /**
       * Send THESE captured credentials and never refresh or retry. Logout revokes with the token
       * it captured a moment before clearing the store, for a session that is deliberately ending.
       */
      revoke?: { token: string };
      public?: never;
    }
  | {
      /** Send NO credentials, and treat a 401 as this request's answer: no refresh, no retry. */
      public: true;
      revoke?: never;
    };

export type ApiCallOptions = CredentialMode & {
  /** Theme GET/PUT run under a 10-second UI deadline and abort their transport. */
  signal?: AbortSignal;
  /**
   * Is the caller that issued this request still alive? Default: assumed yes.
   *
   * SESSION OWNERSHIP IS NOT ENOUGH (A3 review, item 6b). The theme runtime's requests belong to
   * a provider that a route change can tear down while the SAME user stays signed in — so every
   * generation/account check still passes, and a late 401 from an abandoned bootstrap would
   * refresh credentials, clear auth and redirect a PUBLIC route to `/login`. Aborting the
   * transport is best effort and does not stop a response already in flight; this predicate is
   * the guard that does. An abandoned request fails as unauthorized and touches nothing.
   */
  callerActive?: () => boolean;
};

/**
 * Low-level fetch wrapper that handles auth headers, JSON serialization,
 * token refresh on 401, and transforms error responses into typed ApiRequestError instances.
 */
/**
 * True for body types where the browser sets `Content-Type` automatically
 * (with the correct boundary, etc.). For these, the fetch wrapper MUST NOT
 * inject `application/json` or the upload will fail.
 */
function isMultipartBody(body: unknown): boolean {
  if (typeof FormData !== 'undefined' && body instanceof FormData) return true;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return true;
  if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) return true;
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return true;
  return false;
}

const sessionExpired = (): ApiRequestError =>
  new ApiRequestError(
    { code: 'UNAUTHORIZED', message: 'Session expired. Please log in again.' },
    401,
  );

async function request<T>(
  path: string,
  options?: RequestInit,
  call?: ApiCallOptions,
): Promise<ApiResponse<T>> {
  const { accessToken, sessionGeneration, user } = useAuthStore.getState();

  // OWNERSHIP IS CAPTURED HERE, AT REQUEST START — never at callback time. Everything below
  // that touches the store, the tokens or the location compares against this pair first, so a
  // response belonging to a session that has since been replaced can write nothing.
  //
  // The ACCOUNT is compared as well as the generation. The generation alone is sufficient while
  // `beginSession`/`clearAuth` are the only writers of user identity — which they are today —
  // but that is an invariant no type enforces, and a future path that swapped the user without
  // bumping the counter would make two different accounts compare equal here. Comparing both
  // costs one field and removes the dependency on that invariant.
  const generation = sessionGeneration;
  const ownerId = user?.id ?? null;
  const owns = (): boolean => {
    const state = useAuthStore.getState();
    return state.sessionGeneration === generation && (state.user?.id ?? null) === ownerId;
  };

  const bearer = call?.public ? null : (call?.revoke?.token ?? accessToken);
  const hasBody = options?.body !== undefined;
  // Phase 4 hardening (L5): multipart/FormData uploads must NOT have
  // Content-Type forced to application/json — let the browser set it with
  // the correct boundary. JSON callers still get the default.
  const skipJsonContentType = hasBody && isMultipartBody(options?.body);
  const headers: HeadersInit = {
    ...(hasBody && !skipJsonContentType ? { 'Content-Type': 'application/json' } : {}),
    ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
    ...options?.headers,
  };

  let response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    ...(call?.signal ? { signal: call.signal } : {}),
  });

  // Handle 401 with token refresh
  // A `public` request sends no bearer, so it never enters this branch; it is named anyway so
  // the refusal to refresh does not rest on the bearer being empty.
  if (response.status === 401 && bearer && !call?.revoke && !call?.public) {
    // An ABANDONED caller recovers nothing: no refresh, no token write, no clearAuth, no
    // redirect. Its own consumer is gone, so there is nobody for a recovery to serve.
    if (call?.callerActive?.() === false) throw sessionExpired();
    // A 401 for a session that is no longer current must not refresh AT ALL: refreshing would
    // spend the old account's rotation, and any outcome of it would be applied to whoever owns
    // the tab now. Fail this request as unauthorized and leave the live session alone.
    if (!owns()) throw sessionExpired();

    const newToken = await refreshForGeneration(generation, { owns, userId: ownerId });

    // Re-checked after the await: the account can change — and the caller can be torn down —
    // DURING the round-trip, which is the whole race. Neither branch below may run then.
    if (!owns() || call?.callerActive?.() === false) throw sessionExpired();

    if (newToken) {
      // Update store with new token
      const { user } = useAuthStore.getState();
      if (user) {
        useAuthStore.getState().setAuth(user, newToken);
      }

      // Retry the original request with the new token. Same multipart
      // skip as the initial request — the auth-refresh path used to also
      // force application/json which would break re-tried uploads.
      const retryHeaders: HeadersInit = {
        ...(hasBody && !skipJsonContentType ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${newToken}`,
        ...options?.headers,
      };

      response = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: retryHeaders,
        ...(call?.signal ? { signal: call.signal } : {}),
      });
    } else {
      // Refresh failed — clear auth and redirect to login
      useAuthStore.getState().clearAuth();
      window.location.href = '/login';
      throw sessionExpired();
    }
  }

  if (!response.ok) {
    // A proxy in front of the API (nginx 502/503/504) answers with an HTML page, not the
    // envelope — surface that as a typed error instead of a JSON SyntaxError.
    const body = (await response.json().catch(() => null)) as Partial<ApiError> | null;
    throw new ApiRequestError(
      body?.error?.code
        ? body.error
        : {
            code: UNEXPECTED_RESPONSE,
            // No retry advice: after a lost answer a write may or may not have happened.
            message: `The server did not answer normally (HTTP ${response.status}).`,
          },
      response.status,
    );
  }

  // 204 No Content — no body to parse
  if (response.status === 204) {
    return {
      success: true,
      data: undefined as T,
      meta: { timestamp: new Date().toISOString(), requestId: '' },
    } as ApiResponse<T>;
  }

  return response.json() as Promise<ApiResponse<T>>;
}

/**
 * Build a fetch body from arbitrary input:
 *   - `FormData` / `Blob` / `ArrayBuffer` / `URLSearchParams` pass through
 *     unmodified so the browser can set the correct Content-Type (Phase 4
 *     hardening L5 — multipart uploads previously had to bypass the shared
 *     client).
 *   - Everything else is JSON.stringified as before.
 */
function buildBody(data: unknown): BodyInit | undefined {
  if (data === undefined) return undefined;
  if (isMultipartBody(data)) return data as BodyInit;
  return JSON.stringify(data);
}

export const api = {
  get: <T>(path: string, call?: ApiCallOptions) => request<T>(path, undefined, call),

  post: <T>(path: string, data?: unknown, call?: ApiCallOptions) =>
    request<T>(
      path,
      {
        method: 'POST',
        ...(data !== undefined ? { body: buildBody(data) } : {}),
      },
      call,
    ),

  put: <T>(path: string, data?: unknown, call?: ApiCallOptions) =>
    request<T>(
      path,
      {
        method: 'PUT',
        ...(data !== undefined ? { body: buildBody(data) } : {}),
      },
      call,
    ),

  // `call` reached PATCH in theme-customization B2: the account preference write runs under the
  // same 10-second deadline, abort signal and `callerActive` guard as the theme GET/PUT above.
  patch: <T>(path: string, data?: unknown, call?: ApiCallOptions) =>
    request<T>(
      path,
      {
        method: 'PATCH',
        ...(data !== undefined ? { body: buildBody(data) } : {}),
      },
      call,
    ),

  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
